// ============================================================================
// Refonte du moteur, lot 6.66 (moteur v61) — les ÉQUIVALENTS « ∼ » dans une feuille de
// limites. Une ligne « A ∼ B » est vraie si A ÷ B tend vers 1 au point. Elle est JUSTIFIÉE
// si B vient de A par un des gestes du cours, appliqué à un endroit où le remplacement est
// permis (A entier, un facteur d'un produit, le haut ou le bas d'un quotient, la base d'une
// puissance à exposant constant, ce qui est sous une racine) :
//   - un équivalent de référence (en 0 : sin u, tan u, arcsin u, arctan u, eᵘ − 1,
//     ln(1 + u) ~ u ; 1 − cos u ~ u² ÷ 2 ; (1 + u)ᵃ − 1 ~ a u ; √(1 + u) − 1 ~ u ÷ 2),
//     u tendant vers 0 ;
//   - le premier terme non nul d'un développement limité établi ;
//   - le terme dominant d'une somme (le reste est négligeable devant lui) ;
//   - un facteur remplacé par un équivalent (jamais l'expression entière).
// Module pur : aucun état de feuille.
// ============================================================================

import * as Q from './kernel/exact';
import { canon, termKey, type Term } from './kernel/term';
import { coef, type Ser } from './kernel/series';
import {
  constValue,
  hasVar,
  sameNear,
  trueLimit,
  type LimPoint,
  type LimVal,
} from './kernel/limit';

/** « ∼ » : la relation d'équivalence d'une ligne */
export const EQUIV = '∼';

export type EquivStep = 'equivReference' | 'equivDL' | 'equivDominant' | 'equivReplace';

const keyOf = (t: Term): string => {
  try {
    return termKey(canon(t));
  } catch {
    return termKey(t);
  }
};
const num = (n: number, d = 1): Term => ({ k: 'num', v: Q.rat(n, d) }) as Term;
const add = (c: Term[]): Term => (c.length === 1 ? c[0]! : ({ k: 'add', c } as Term));
const mul = (c: Term[]): Term => ({ k: 'mul', c }) as Term;
const div = (a: Term, b: Term): Term => ({ k: 'div', a, b }) as Term;
const pow = (a: Term, b: Term): Term => ({ k: 'pow', a, b }) as Term;
const fn = (f: string, a: Term): Term => ({ k: 'fn', f, a }) as Term;

/** la limite de t au point (null : inconnue) */
function limitOf(t: Term, P: LimPoint): LimVal | null {
  try {
    return trueLimit(t, P).v;
  } catch {
    return null;
  }
}
const tendsTo = (t: Term, P: LimPoint, v: number): boolean => {
  const L = limitOf(t, P);
  return !!L && L.k === 'fin' && Math.abs(L.v - v) < 1e-9;
};

/** la limite du quotient A ÷ B (null : on ne sait pas) */
export function equivRatio(A: Term, B: Term, P: LimPoint): LimVal | null {
  return limitOf(div(A, B), P);
}

/** A ∼ B au point */
export function equivHolds(A: Term, B: Term, P: LimPoint): boolean {
  if (isZeroConst(B, P)) return false;
  const L = equivRatio(A, B, P);
  return !!L && L.k === 'fin' && Math.abs(L.v - 1) < 1e-9;
}

/** t vaut 0 près du point (« 0 », « x − x ») */
export function isZeroConst(t: Term, P: LimPoint): boolean {
  if (hasVar(t, P.x)) return sameNear(t, num(0), P);
  try {
    return Math.abs(constValue(t)) < 1e-12;
  } catch {
    return false;
  }
}

/** « −t » ou « (−1) × t » : t */
function negOf(t: Term): Term | null {
  if (t.k !== 'mul') return null;
  const minus = t.c.findIndex((c) => c.k === 'num' && Q.eq((c as { v: Q.Rat }).v, Q.rat(-1)));
  if (minus < 0) return null;
  const rest = t.c.filter((_, i) => i !== minus);
  return rest.length === 1 ? rest[0]! : mul(rest);
}

/** l'équivalent de référence de s (null : pas une forme du cours) */
export function referenceEquiv(s: Term, P: LimPoint): Term | null {
  const x = P.x;
  if (s.k === 'fn') {
    if (['sin', 'tan', 'arcsin', 'arctan'].includes(s.f) && tendsTo(s.a, P, 0)) return s.a;
    // ln(u), u → 1 : ln(u) ∼ u − 1
    if (s.f === 'ln' && tendsTo(s.a, P, 1)) return add([s.a, num(-1)]);
    return null;
  }
  if (s.k !== 'add' || s.c.length !== 2) return null;
  const ci = s.c.findIndex((c) => !hasVar(c, x));
  if (ci < 0) return null;
  let c: number;
  try {
    c = constValue(s.c[ci]!);
  } catch {
    return null;
  }
  const g = s.c[1 - ci]!;
  if (Math.abs(c + 1) < 1e-12) {
    // eᵘ − 1 ∼ u
    if (g.k === 'fn' && g.f === 'exp' && tendsTo(g.a, P, 0)) return g.a;
    // √a − 1 ∼ (a − 1) ÷ 2, a → 1
    if (g.k === 'fn' && g.f === 'sqrt' && tendsTo(g.a, P, 1))
      return div(add([g.a, num(-1)]), num(2));
    // cos(u) − 1 ∼ −u² ÷ 2
    if (g.k === 'fn' && g.f === 'cos' && tendsTo(g.a, P, 0))
      return mul([num(-1), div(pow(g.a, num(2)), num(2))]);
    if (g.k === 'pow') {
      // aᵇ − 1 ∼ b × ln(a) quand b × ln(a) → 0 (a > 0)
      if (hasVar(g.b, x)) {
        const w = mul([g.b, fn('ln', g.a)]);
        return tendsTo(w, P, 0) ? w : null;
      }
      // (1 + u)ʳ − 1 ∼ r × u, a = 1 + u → 1
      if (tendsTo(g.a, P, 1)) return mul([g.b, add([g.a, num(-1)])]);
    }
    return null;
  }
  // 1 − cos(u) ∼ u² ÷ 2
  if (Math.abs(c - 1) < 1e-12) {
    const n = negOf(g);
    if (n && n.k === 'fn' && n.f === 'cos' && tendsTo(n.a, P, 0))
      return div(pow(n.a, num(2)), num(2));
  }
  return null;
}

/** les termes d'une somme, sommes imbriquées aplaties */
function termsOfSum(t: Term): Term[] {
  if (t.k !== 'add') return [t];
  return t.c.flatMap(termsOfSum);
}

/** r est une partie des termes de s, et le reste est négligeable devant r */
export function isDominantPart(s: Term, r: Term, P: LimPoint): boolean {
  if (s.k !== 'add') return false;
  const T = termsOfSum(s);
  if (T.length < 2 || T.length > 8) return false;
  for (let mask = 1; mask < (1 << T.length) - 1; mask++) {
    const part = T.filter((_, i) => mask & (1 << i));
    const rest = T.filter((_, i) => !(mask & (1 << i)));
    if (!sameNear(add(part), r, P)) continue;
    if (tendsTo(div(add(rest), r), P, 0)) return true;
  }
  return false;
}

/** le premier terme non nul d'un développement en 0 : c × xᵈ (null : aucun connu) */
export function leadingTerm(ser: Ser, x: string): Term | null {
  const degs = [...ser.co.keys()].filter((d) => d <= ser.e).sort((a, b) => a - b);
  for (const d of degs) {
    const c = coef(ser, d);
    if (Q.isZero(c)) continue;
    const X = { k: 'sym', n: x } as Term;
    const xd = d === 0 ? num(1) : d === 1 ? X : pow(X, num(d));
    return Q.eq(c, Q.ONE) ? xd : mul([{ k: 'num', v: c } as Term, xd]);
  }
  return null;
}

/**
 * Les endroits où B diffère de A, en ne descendant que là où remplacer par un équivalent
 * est permis (facteurs, haut et bas d'un quotient, base d'une puissance à exposant constant,
 * sous une racine). Chaque paire [s, r] : s (dans A) devient r (dans B).
 */
export function equivDiff(A: Term, B: Term, x: string): [Term, Term][] {
  if (keyOf(A) === keyOf(B)) return [];
  if (A.k === 'mul' && B.k === 'mul' && A.c.length === B.c.length)
    return A.c.flatMap((a, i) => equivDiff(a, B.c[i]!, x));
  if (A.k === 'div' && B.k === 'div') return [...equivDiff(A.a, B.a, x), ...equivDiff(A.b, B.b, x)];
  if (A.k === 'pow' && B.k === 'pow' && !hasVar(A.b, x) && keyOf(A.b) === keyOf(B.b))
    return equivDiff(A.a, B.a, x);
  if (A.k === 'fn' && B.k === 'fn' && A.f === 'sqrt' && B.f === 'sqrt')
    return equivDiff(A.a, B.a, x);
  return [[A, B]];
}

/**
 * Comment justifier s ∼ r (déjà VRAI) : référence, DL, terme dominant, ou facteur
 * remplacé (seulement si s n'est pas l'expression entière). null : pas un geste.
 */
export function classifyEquiv(
  s: Term,
  r: Term,
  P: LimPoint,
  whole: boolean,
  dlOf: (t: Term) => Ser | null,
): EquivStep | null {
  const R = referenceEquiv(s, P);
  if (R && sameNear(R, r, P)) return 'equivReference';
  const ser = dlOf(s);
  if (ser) {
    const lead = leadingTerm(ser, P.x);
    if (lead && sameNear(lead, r, P)) return 'equivDL';
  }
  if (isDominantPart(s, r, P)) return 'equivDominant';
  return whole ? null : 'equivReplace';
}
